import React, { useCallback, useEffect, useState } from 'react';
import { FiCheck, FiLock, FiPlus, FiRefreshCw } from 'react-icons/fi';
import { createDwAgent, getDwAgent, updateDwAgent } from '../claudeagents/claudeAgentApi';
import { AgentLogoControl, AGENT_EFFORT_OPTIONS, AGENT_MODELS, AVATAR_COLORS, DEFAULT_AGENT_EFFORT, DwIntegrationsTab, DwMcpTab, DwMemoryTab, DwSkillsTab, getModelEfforts, getModelId, MdEditor, ModelPicker, modelSupportsFast, normalizeAgentRuntime, readAgentRuntime, saveMeta, type AgentEffort, type AgentSpeed, type PendingAgentLogo, } from '../digitalworkforce/DigitalWorkforcePage';
import { setAgentPhotoFromFile } from '../../utils/agentPhotos';
import '../../styles/DigitalWorkforce.css';

type EditorTab = 'overview' | 'agents' | 'tools' | 'mcp' | 'integrations' | 'memory';

// Digital Workforce's tabs, minus Activity: a publish wizard has no run history to show.
const TABS: { id: EditorTab; label: string; locked: boolean }[] = [
    { id: 'overview', label: 'Overview', locked: false },
    { id: 'agents', label: 'agents.md', locked: false },
    { id: 'tools', label: 'skills.md', locked: true },
    { id: 'mcp', label: 'Tools', locked: true },
    // Integrations tab hidden; add { id: 'integrations', label: 'Integrations', locked: false } back to show it.
    { id: 'memory', label: 'Memory', locked: false },
];

const LOCKED_MESSAGES: Record<EditorTab, string> = {
    overview: '',
    agents: '',
    tools: 'Save the agent first — skills can only be attached after the agent has been created.',
    mcp: 'Save the agent first — MCP servers can only be connected after the agent has been created.',
    integrations: 'Save the agent first — integrations can only be enabled after the agent has been created.',
    memory: 'Save the agent first — memory is stored per agent, so it only exists after the agent has been created.',
};

interface Draft {
    name: string;
    model: string;
    effort: AgentEffort | '';
    speed: AgentSpeed;
    description: string;
    agentsMd: string;
}

const BLANK: Draft = { name: '', model: '', effort: DEFAULT_AGENT_EFFORT, speed: 'standard', description: '', agentsMd: '' };

interface NewAgentEditorProps {
    /** Set once the agent exists; the editor then loads it and unlocks the attach tabs. */
    agentId: string;
    /** Names already used by this person's agents; Digital Workforce refuses duplicates. */
    takenNames: string[];
    onCreated: (agentId: string) => void;
    /** Called when the agent may have changed (a save, or leaving a tab where things get
     *  attached), so an agent listing can tell whether it's behind the agent. */
    onActivity?: () => void;
}

/**
 * Digital Workforce's New Agent editor, inside the publish wizard: the same header, tabs,
 * Overview fields and Create Agent button, and - once the agent exists - Digital
 * Workforce's own skills.md, Tools, Integrations and Memory tabs to attach things to it.
 * The agent is a normal Digital Workforce agent, so it can be edited there later too.
 */
const NewAgentEditor: React.FC<NewAgentEditorProps> = ({ agentId, takenNames, onCreated, onActivity }) => {
    const [draft, setDraft] = useState<Draft>(BLANK);
    const [savedDraft, setSavedDraft] = useState<Draft>(BLANK);
    const [tab, setTab] = useState<EditorTab>('overview');
    const [pendingLogo, setPendingLogo] = useState<PendingAgentLogo | null>(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [attempted, setAttempted] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [modelPickerOpen, setModelPickerOpen] = useState(false);
    const isCreating = !agentId;

    // Coming back to Setup after the agent was made: show it as it is now.
    useEffect(() => {
        if (!agentId)
            return;
        let cancelled = false;
        setLoading(true);
        getDwAgent(agentId)
            .then(rec => {
            if (cancelled)
                return;
            const model = getModelId(rec.model);
            const runtime = readAgentRuntime(rec, model);
            const loaded: Draft = { name: rec.name ?? '', model, effort: runtime.effort, speed: runtime.speed, description: rec.description ?? '', agentsMd: rec.system ?? '' };
            setDraft(loaded);
            setSavedDraft(loaded);
        })
            .catch(() => { })
            .finally(() => { if (!cancelled)
            setLoading(false); });
        return () => { cancelled = true; };
    }, [agentId]);

    useEffect(() => () => { if (pendingLogo)
        URL.revokeObjectURL(pendingLogo.preview); }, [pendingLogo]);

    const update = <K extends keyof Draft>(key: K, value: Draft[K]) => {
        setSaved(false);
        setDraft(d => ({ ...d, [key]: value }));
    };
    const handleModelChange = useCallback((model: string) => {
        setSaved(false);
        setDraft(d => ({ ...d, model, ...normalizeAgentRuntime(model, d.effort, d.speed) }));
    }, []);

    const trimmedName = draft.name.trim();
    const nameRequiredInvalid = !trimmedName;
    const nameReservedInvalid = /^default\b/i.test(trimmedName);
    const nameDuplicateInvalid = !!trimmedName && takenNames.some(n => n.trim().toLowerCase() === trimmedName.toLowerCase()
        && n.trim().toLowerCase() !== savedDraft.name.trim().toLowerCase());
    const modelRequiredInvalid = !draft.model.trim();
    const canSave = !nameRequiredInvalid && !nameReservedInvalid && !nameDuplicateInvalid && !modelRequiredInvalid;
    const dirty = JSON.stringify(draft) !== JSON.stringify(savedDraft);
    const showFieldValidation = attempted;

    const handleSave = async () => {
        setAttempted(true);
        if (!canSave || saving)
            return;
        setSaving(true);
        setError('');
        const runtime = normalizeAgentRuntime(draft.model, draft.effort, draft.speed);
        const payload = {
            name: trimmedName,
            description: draft.description,
            system: draft.agentsMd,
            model: draft.model,
            effort: runtime.effort || null,
            speed: runtime.speed,
        };
        try {
            if (isCreating) {
                const rec = await createDwAgent({ ...payload, soul: '', heartbeat: '' });
                if (pendingLogo) {
                    // Best-effort, as in Digital Workforce: the agent exists either way.
                    try { await setAgentPhotoFromFile(rec.agent_id, pendingLogo.file); }
                    catch { /* ignore */ }
                }
                saveMeta(rec.agent_id, { description: draft.description, model: draft.model, avatarColor: AVATAR_COLORS[0], agentsMd: draft.agentsMd });
                setSavedDraft({ ...draft, name: trimmedName });
                setPendingLogo(null);
                setAttempted(false);
                onCreated(rec.agent_id);
            }
            else {
                saveMeta(agentId, { description: draft.description, model: draft.model, agentsMd: draft.agentsMd });
                await updateDwAgent(agentId, payload);
                setSavedDraft({ ...draft, name: trimmedName });
                setSaved(true);
                onActivity?.();
            }
        }
        catch (err: unknown) {
            setError(err instanceof Error && err.message ? err.message : `Couldn't ${isCreating ? 'create' : 'save'} the agent. Please try again.`);
        }
        finally {
            setSaving(false);
        }
    };

    const modelLabel = AGENT_MODELS.find(m => m.value === draft.model)?.label ?? draft.model;
    const hasModel = !!draft.model.trim();
    const effortOptions = AGENT_EFFORT_OPTIONS.filter(o => getModelEfforts(draft.model).includes(o.value));
    const fastAvailable = modelSupportsFast(draft.model);
    const effortHint = AGENT_EFFORT_OPTIONS.find(o => o.value === draft.effort)?.hint;
    const showSaveButton = tab === 'overview' || tab === 'agents';
    const lockedBody = (id: EditorTab) => (<div className="dw-tab-content dw-tab-locked">
        <FiLock size={24} style={{ opacity: 0.25, marginBottom: 10 }}/>
        <div className="dw-tab-locked__title">Agent ID required</div>
        <div className="dw-tab-locked__msg">{LOCKED_MESSAGES[id]}</div>
      </div>);

    return (<div className="dw-panel ms-dw-embed">
      <div className="dw-panel__header">
        <AgentLogoControl agentId={agentId} name={trimmedName || 'New Agent'} color={AVATAR_COLORS[0]} isSystem={false} editable draft={isCreating ? {
            logo: pendingLogo,
            onPick: (file: File) => setPendingLogo(prev => { if (prev)
                URL.revokeObjectURL(prev.preview); return { file, preview: URL.createObjectURL(file) }; }),
            onClear: () => setPendingLogo(prev => { if (prev)
                URL.revokeObjectURL(prev.preview); return null; }),
        } : undefined}/>
        <div className="dw-panel__title-area">
          <div className="dw-panel__name">{trimmedName || 'New Agent'}</div>
          <div className="dw-panel__role-type">{modelLabel || 'Select model'}</div>
        </div>
      </div>

      <div className="dw-tabs" role="tablist" aria-label="Agent sections">
        {TABS.map(t => {
            const isLocked = isCreating && t.locked;
            return (<button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={`dw-tab${tab === t.id ? ' dw-tab--active' : ''}${isLocked ? ' dw-tab--locked' : ''}`} onClick={() => { if (isLocked) return; setTab(t.id); onActivity?.(); }} disabled={isLocked} title={isLocked ? 'Create the agent first to enable this tab' : undefined}>
              {t.label}
              {isLocked && <FiLock size={10} style={{ marginLeft: 4, opacity: 0.5 }}/>}
            </button>);
        })}
      </div>

      <div className="dw-tab-body">
        {tab === 'overview' && (<div className="dw-tab-content">
            <div className="dw-agent-ov">
              <div className="dw-agent-details dw-agent-details--ov">
                <div className="dw-agent-details__row">
                  <div className="dw-field dw-field--grow">
                    <label className="dw-field__label">Name</label>
                    <input className="dw-field__input" placeholder="e.g. Aria" value={draft.name} disabled={loading} onChange={e => update('name', e.target.value)} style={(showFieldValidation && (nameRequiredInvalid || nameReservedInvalid)) || nameDuplicateInvalid ? { borderColor: '#ef4444' } : undefined}/>
                    {showFieldValidation && nameRequiredInvalid && (<p className="dw-fp-field-error">Please enter agent name.</p>)}
                    {showFieldValidation && !nameRequiredInvalid && nameReservedInvalid && (<p className="dw-fp-field-error">Names starting with "Default" are reserved.</p>)}
                    {nameDuplicateInvalid && !nameRequiredInvalid && !nameReservedInvalid && (<p className="dw-fp-field-error">Agent name already exists. Use a unique name.</p>)}
                  </div>
                  <div className="dw-field dw-field--grow">
                    <label className="dw-field__label">Model *</label>
                    <ModelPicker value={draft.model} onChange={handleModelChange} disabled={loading} invalid={showFieldValidation && modelRequiredInvalid} onOpenChange={setModelPickerOpen}/>
                    {!modelPickerOpen && showFieldValidation && modelRequiredInvalid && (<p className="dw-fp-field-error">Please select a model.</p>)}
                    {!modelPickerOpen && !modelRequiredInvalid && AGENT_MODELS.find(m => m.value === draft.model)?.hint && (<p className="dw-field__hint">{AGENT_MODELS.find(m => m.value === draft.model)?.hint}</p>)}
                  </div>
                </div>
                <div className="dw-agent-details__row dw-agent-details__row--runtime">
                  <div className="dw-field dw-field--grow">
                    <label className="dw-field__label">Thinking effort</label>
                    {!hasModel ? (<>
                        <div className="dw-field__select-wrap">
                          <select className="dw-field__select" value={DEFAULT_AGENT_EFFORT} disabled>
                            <option value={DEFAULT_AGENT_EFFORT}>Medium (default)</option>
                          </select>
                        </div>
                        <p className="dw-field__hint">Select a model first — available levels depend on the model.</p>
                      </>) : effortOptions.length > 0 ? (<>
                        <div className="dw-field__select-wrap">
                          <select className="dw-field__select" value={draft.effort || DEFAULT_AGENT_EFFORT} disabled={loading} onChange={e => update('effort', e.target.value as AgentEffort)}>
                            {effortOptions.map(o => (<option key={o.value} value={o.value}>
                                {o.label}{o.value === DEFAULT_AGENT_EFFORT ? ' (default)' : ''}
                              </option>))}
                          </select>
                        </div>
                        {effortHint && <p className="dw-field__hint">{effortHint}</p>}
                      </>) : (<>
                        <input className="dw-field__input" value="Not supported" disabled readOnly/>
                        <p className="dw-field__hint">{modelLabel} doesn't support effort levels.</p>
                      </>)}
                  </div>
                  <div className="dw-field dw-field--grow">
                    <label className="dw-field__label">Speed</label>
                    <div className="dw-field__select-wrap">
                      <select className="dw-field__select" value={fastAvailable ? draft.speed : 'standard'} disabled={loading || !hasModel || !fastAvailable} onChange={e => update('speed', e.target.value as AgentSpeed)}>
                        <option value="standard">Normal</option>
                        <option value="fast">Fast</option>
                      </select>
                    </div>
                    <p className="dw-field__hint">
                      {!hasModel
            ? 'Select a model first — fast mode is available on Opus 4.8.'
            : !fastAvailable
                ? `Fast mode isn't available on ${modelLabel} — available on Opus 4.8.`
                : draft.speed === 'fast'
                    ? 'Up to ~2.5x faster responses at premium pricing.'
                    : 'Standard speed and pricing.'}
                    </p>
                  </div>
                </div>
                <div className="dw-field">
                  <label className="dw-field__label">Description</label>
                  <textarea className="dw-field__input dw-field__textarea dw-field__textarea--lg" placeholder="Describe what this agent does…" value={draft.description} rows={8} disabled={loading} onChange={e => update('description', e.target.value)}/>
                </div>
              </div>
            </div>
          </div>)}

        {tab === 'agents' && (<div className="dw-tab-content">
            <MdEditor value={draft.agentsMd} onChange={v => update('agentsMd', v)} placeholder={`# ${trimmedName || 'Agent'} — Agents\n\n## Collaborators\n- List other agents this agent works with\n\n## Handoffs\n- Conditions under which this agent hands off to another\n\n## Escalation\n- When to escalate to a human`} minHeight={320}/>
          </div>)}

        {tab === 'tools' && (isCreating ? lockedBody('tools') : (<div className="dw-panel__tab-flex-fill"><DwSkillsTab agentId={agentId} agentName={trimmedName}/></div>))}
        {tab === 'mcp' && (isCreating ? lockedBody('mcp') : (<div className="dw-panel__tab-flex-fill"><DwMcpTab agentId={agentId} agentName={trimmedName}/></div>))}
        {tab === 'integrations' && (isCreating ? lockedBody('integrations') : (<div className="dw-panel__tab-flex-fill"><DwIntegrationsTab agentId={agentId} agentName={trimmedName || 'This agent'}/></div>))}
        {tab === 'memory' && (isCreating ? lockedBody('memory') : (<div className="dw-tab-content"><DwMemoryTab agentId={agentId}/></div>))}
      </div>

      {(error || showSaveButton) && (<div className="dw-panel__footer">
          {error ? <span className="dw-panel__footer-error">{error}</span> : null}
          {showSaveButton && (<button type="button" className={`dw-save-btn${saved ? ' dw-save-btn--saved' : ''}`} onClick={() => { void handleSave(); }} disabled={!canSave || saving || loading || (!isCreating && !dirty)}>
              {saved && !dirty ? (<><FiCheck size={13}/> Saved</>)
                : saving ? (<><FiRefreshCw size={13} style={{ animation: 'dw-spin 0.8s linear infinite' }}/> {isCreating ? 'Creating…' : 'Saving…'}</>)
                    : isCreating ? (<><FiPlus size={13}/> Create Agent</>)
                        : (<><FiCheck size={13}/> Save Changes</>)}
            </button>)}
        </div>)}
    </div>);
};

export default NewAgentEditor;
